import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BlockVideoService } from 'src/app/core/services/block-video.service';
import { EditVideoBlock, VideoBlock } from 'src/app/models/block-video';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-form-video',
  templateUrl: './form-video.component.html',
  styleUrls: ['./form-video.component.css']
})
export class FormVideoComponent {
  formsVideo: FormGroup;
  tagsCms: { id: number; slug: string; nombre: string }[] = [];
  dropdownOpenTags = false;
  selectedTags: string[] = [];
  tagSearch: string = '';
  isEditMode: boolean = false;
  tagAvailability: boolean | null = null;
  parametroId: number = 0;

  constructor(private videoServices: BlockVideoService, private fb: FormBuilder, private router: Router, private route: ActivatedRoute) { }


  ngOnInit(): void {
    this.formsVideo = this.fb.group({
      titulo: ['', Validators.required],
      embebido: ['', [
        Validators.required,
        Validators.pattern(/^<iframe.*src="https:\/\/www\.youtube\.com\/embed\/.*".*<\/iframe>$/)
      ]],
      tags: [[], Validators.required],
      contenido: ['', Validators.required],
      tagSearch: [''],
      estado: ['activo']
    });

    this.loadTagsVideo();

    //trae el id del home
    this.route.queryParams.subscribe(params => {
      this.parametroId = +params['id'];
      this.getVideoBlock(this.parametroId);
    });
  }

  getVideoBlock(id: number) {
    if (!id) return;

    this.videoServices.getSearchVideo(id).subscribe({
      next: (response) => {
        console.log('Bloque recibido:', response);

        if (response && response.data) {
          this.isEditMode = true;

          // Actualizar valores del formulario con la data recibida
          this.formsVideo.patchValue({
            titulo: response.data.titulo,
            contenido: response.data.contenido,
            embebido: response.data.embebido,
            tags: response.data.tags,
            estado: response.data.estado
          });

          // Para reflejar las tags seleccionadas en la UI
          this.selectedTags = response.data.tags;
        }
      },
      error: (err) => {
        console.error('Error al cargar bloque', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la información del bloque de video.',
          confirmButtonColor: '#ef4444'
        });
      }
    });
  }

  //PARA LOS TAGS
  loadTagsVideo(): void {
    this.videoServices.getTagsCMS()

      .then(data => {
        this.tagsCms = data;
        console.log('Tags CMS:', data);
      })
      .catch(error => {
        console.error('Error al cargar Tags:', error);
      });
  }


  get selectedTagsValue(): string[] {
    return (this.formsVideo.get('tags')?.value as string[]) ?? [];
  }

  // valor actual del input de búsqueda tomado del form
  get tagSearchValue(): string {
    return (this.formsVideo.get('tagSearch')?.value || '').toString();
  }

  // filtro real (ahora también busca por slug)
  get filteredTags(): { id: number; slug: string; nombre: string }[] {
    const q = this.tagSearchValue.trim().toLowerCase();
    if (!q) return this.tagsCms;
    return this.tagsCms.filter(t =>
      (t.nombre || '').toLowerCase().includes(q) ||
      (t.slug || '').toLowerCase().includes(q)
    );
  }

  isTagSelected(nombre: string): boolean {
    return this.selectedTagsValue.includes(nombre);
  }


  toggleTag(tagName: string) {
    const current = [...this.selectedTagsValue];
    const exists = current.includes(tagName);

    if (exists) {
      // Quitar tag
      const next = current.filter(x => x !== tagName);
      if (next.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Atención',
          text: 'Debes seleccionar al menos un tag.',
          confirmButtonColor: '#7e3af2'
        });
      }
      this.formsVideo.get('tags')?.setValue(next);
      this.selectedTags = next;
    } else {
      // Antes de agregar, validamos disponibilidad
      this.videoServices.getValidateTagAvailability(tagName).subscribe({
        next: (response) => {
          if (response.data) {
            // Está disponible → agregar
            if (current.length >= 5) {
              Swal.fire({
                icon: 'warning',
                title: 'Límite alcanzado',
                text: 'Solo puedes seleccionar un máximo de 5 tags.',
                confirmButtonColor: '#7e3af2'
              });
              return;
            }

            const next = [...current, tagName];
            this.formsVideo.get('tags')?.setValue(next);
            this.selectedTags = next;
            this.formsVideo.get('tags')?.markAsDirty();
          } else {
            // NO disponible → alerta con opción "Reemplazar"
            Swal.fire({
              icon: 'warning',
              title: 'Tag en uso',
              text: `Este tag "${tagName}" ya se encuentra en uso en otro bloque`,
              showCancelButton: true,
              confirmButtonColor: '#3C00A3',
              cancelButtonColor: '#D6E0FF',
              confirmButtonText: 'Reemplazar',
              cancelButtonText: 'Cancelar',
              customClass: {
                cancelButton: 'swal-cancel-text-color'
              }
            }).then((result) => {
              if (result.isConfirmed) {
                // Forzar agregar aunque esté ocupado
                if (current.length >= 5) {
                  Swal.fire({
                    icon: 'warning',
                    title: 'Límite alcanzado',
                    text: 'Solo puedes seleccionar un máximo de 5 tags.',
                    confirmButtonColor: '#7e3af2'
                  });
                  return;
                }
                const next = [...current, tagName];
                this.formsVideo.get('tags')?.setValue(next);
                this.selectedTags = next;
                this.formsVideo.get('tags')?.markAsDirty();
              }
            });
          }
        },
        error: (err) => {
          console.error('Error al validar tag', err);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo validar la disponibilidad de la tag.',
            confirmButtonColor: '#ef4444'
          });
        }
      });
    }
  }


  removeTag(tagName: string): void {
    const current = [...this.selectedTagsValue];
    const next = current.filter((x) => x !== tagName);
    // si queda 0 avisamos
    if (next.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Atención',
        text: 'Debes seleccionar al menos un tag.',
        confirmButtonColor: '#7e3af2'
      });
    }
    this.formsVideo.get('tags')?.setValue(next);
    this.formsVideo.get('tags')?.markAsDirty();
    this.selectedTags = next;
  }

  getTagName(id: number): string {
    const found = this.tagsCms.find((t) => t.id === id);
    return found ? found.nombre : '';
  }

  trackByTag(index: number, item: { id: number; slug: string; nombre: string }): number {
    return item.id;
  }


  // Confirmación con SweetAlert al cambiar estado
  onToggleStatus(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;

    if (!checked) {
      Swal.fire({
        icon: 'warning',
        title: '¿Inactivar bloque?',
        text: 'Si lo inactivas, este bloque de video no será visible en la página.',
        showCancelButton: true,
        confirmButtonText: 'Sí, inactivar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280'
      }).then(result => {
        if (result.isConfirmed) {
          this.formsVideo.get('estado')?.setValue('Inactivo');
        } else {
          this.formsVideo.get('estado')?.setValue('Activo', { emitEvent: false });
        }
      });
    } else {
      Swal.fire({
        icon: 'question',
        title: '¿Activar bloque?',
        text: 'El bloque de video será visible en la página.',
        showCancelButton: true,
        confirmButtonText: 'Sí, activar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#7e3af2',
        cancelButtonColor: '#6b7280'
      }).then(result => {
        if (result.isConfirmed) {
          this.formsVideo.get('estado')?.setValue('Activo');
        } else {
          this.formsVideo.get('estado')?.setValue('Inactivo', { emitEvent: false });
        }
      });
    }
  }


  submitForm() {
    if (this.formsVideo.invalid) {
      Swal.fire('Formulario incompleto', 'Falta información, complete todos los campos', 'warning');
      return;
    }

    if ((this.selectedTagsValue || []).length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Debes seleccionar al menos un tag.',
        confirmButtonColor: '#7e3af2'
      });
      return;
    }

    const formValue = this.formsVideo.value;

    if (this.isEditMode) {
      // EDITAR BLOQUE
      const payload: EditVideoBlock = {
        id: this.parametroId,
        titulo: formValue.titulo,
        contenido: formValue.contenido,
        tags: formValue.tags,
        embebido: formValue.embebido,
        estado: formValue.estado
      };

      this.videoServices.editVideoBlock(payload).subscribe({
        next: (res) => {
          console.log('Bloque actualizado:', res);
          Swal.fire({
            icon: 'success',
            title: 'Bloque actualizado',
            text: 'El bloque de video se ha actualizado correctamente.',
            confirmButtonColor: '#7e3af2'
          }).then(() => this.router.navigate(['/home-video']));
        },
        error: (err) => {
          console.error('Error al actualizar:', err);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo actualizar el bloque de video.',
            confirmButtonColor: '#ef4444'
          });
        }
      });
    } else {
      // CREAR NUEVO BLOQUE
      const payload: VideoBlock = {
        titulo: formValue.titulo,
        contenido: formValue.contenido,
        tags: formValue.tags,
        embebido: formValue.embebido,
        estado: formValue.estado
      };

      console.log('envio forms', payload);

      this.videoServices.saveVideoBlock(payload).subscribe({
        next: (response) => {
          console.log(' Respuesta del servidor:', response);
          Swal.fire({
            icon: 'success',
            title: 'Bloque guardado',
            text: 'El bloque de video se ha guardado correctamente.',
            confirmButtonColor: '#7e3af2'
          }).then(() => this.router.navigate(['/home-video']));
        },
        error: (err) => {
          console.error(' Error al guardar:', err);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo guardar el bloque de video.',
            confirmButtonColor: '#ef4444'
          });
        }
      });
    }
  }
}
